import { BaseComponent } from '../base-component'
import {
  autoUpdate,
  computePosition,
  offset,
  flip,
  shift,
  arrow,
} from '@floating-ui/dom'
import { getTemplateFirstChild } from '../../utils/dom'
import { $el } from 'src/scripts/utils/utils'

const ARROW_SELECTOR = '[data-float-element-arrow]'

export class ClipboardButtonTooltip extends BaseComponent {
  element: HTMLElement
  removeUpdate: () => void = () => {}

  constructor() {
    super()

    this.element = getTemplateFirstChild(
      $el(`[data-clipboard-button-tooltip-template="${this.id}"]`, this),
    )
  }

  updatePosition({ x, y }): void {
    Object.assign(this.style, {
      left: `${x}px`,
      top: `${y}px`,
    })
  }

  getOptions() {
    const arrowElement = $el(ARROW_SELECTOR, this.element)

    const middleware = [
      offset({
        alignmentAxis: +(this.dataset.alignmentAxisOffset || 0),
        mainAxis: +(this.dataset.mainAxisOffset || 0),
      }),
      flip(),
      shift(),
    ]

    if (arrowElement) {
      middleware.push(arrow({ element: arrowElement }))
    }

    return {
      placement: this.dataset.placement || 'bottom-end',
      middleware,
      strategy: 'absolute',
    }
  }

  updateArrow({ placement, middlewareData }): void {
    const arrowElement = $el(ARROW_SELECTOR, this.element)

    if (!arrowElement) {
      return
    }

    const { x: arrowX, y: arrowY } = middlewareData.arrow

    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[placement.split('-')[0]]

    Object.assign(arrowElement.style, {
      left: arrowX != null ? `${arrowX}px` : '',
      top: arrowY != null ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide]: '-5px',
    })
  }

  mount(): void {
    const floatElement = $el(
      `[data-clipboard-button-tooltip-target-id="${this.id}"]`,
    )

    this.replaceChildren(this.element)

    if (!floatElement) {
      return
    }

    this.removeUpdate = autoUpdate(floatElement, this, () => {
      computePosition(floatElement, this, this.getOptions() as any).then(
        data => {
          this.updatePosition(data)
          this.updateArrow(data)
        },
      )
    })
  }

  unmount(): void {
    this.replaceChildren()
    this.removeUpdate()
  }

  show = (): void => {
    this.mount()
    this.setVisible(true)
    this.emit('show', {})
  }

  hide = (): void => {
    this.setVisible(false)
    this.unmount()
    this.emit('hide', {})
  }

  setVisible(isVisible: boolean): void {
    this.setAttribute('aria-hidden', isVisible ? 'false' : 'true')
  }
}
